import type {
  AgentBody,
  AnchorProfileKind,
  BaseEntity,
  ProfileCategory,
  EntityPluginContext,
  SkillData,
} from "@brains/plugins";
import {
  BaseEntityAdapter,
  anchorProfileKindSchema,
  baseEntitySchema,
  parseAgentBody,
  skillDataSchema,
} from "@brains/plugins";
import { normalizeTags } from "@brains/utils/string-utils";
import { z } from "@brains/utils/zod";

export interface CapabilityProfileSkill {
  name: string;
  description: string;
  tags: string[];
  examples?: string[];
}

export interface CapabilityProfile {
  id: string;
  source: "self" | "agent";
  name: string;
  brainName?: string;
  kind?: AnchorProfileKind;
  status?: "approved" | "discovered" | "archived";
  description?: string;
  notes?: string;
  skills: CapabilityProfileSkill[];
}

const capabilityAgentFrontmatterSchema = z.object({
  name: z.string(),
  kind: anchorProfileKindSchema,
  organization: z.string().optional(),
  brainName: z.string(),
  url: z.string().url(),
  did: z.string().optional(),
  status: z.enum(["approved", "discovered", "archived"]),
  discoveredAt: z.string().datetime().optional(),
});

type CapabilityAgentFrontmatter = z.infer<
  typeof capabilityAgentFrontmatterSchema
>;

const capabilityAgentEntitySchema = baseEntitySchema.extend({
  entityType: z.literal("agent"),
  metadata: z.record(z.string(), z.unknown()),
});

class CapabilityAgentAdapter extends BaseEntityAdapter<
  BaseEntity,
  Record<string, unknown>
> {
  constructor() {
    super({
      entityType: "agent",
      purpose: "An assessed profile of a subject's capabilities.",
      schema: capabilityAgentEntitySchema,
      frontmatterSchema: capabilityAgentFrontmatterSchema,
    });
  }

  public fromMarkdown(markdown: string): Partial<BaseEntity> {
    return { content: markdown, entityType: "agent" };
  }

  public parseAgent(entity: BaseEntity): {
    frontmatter: CapabilityAgentFrontmatter;
    body: AgentBody;
  } | null {
    const frontmatterResult = capabilityAgentFrontmatterSchema.safeParse(
      this.parseFrontMatter(entity.content, capabilityAgentFrontmatterSchema),
    );
    if (!frontmatterResult.success) return null;

    return {
      frontmatter: frontmatterResult.data,
      body: parseAgentBody(this.extractBody(entity.content)),
    };
  }
}

const agentAdapter = new CapabilityAgentAdapter();

function asProfileSkill(skill: SkillData): CapabilityProfileSkill {
  return {
    name: skill.name,
    description: skill.description,
    tags: normalizeTags(skill.tags),
    examples: skill.examples,
  };
}

function parseSkillEntity(entity: BaseEntity): SkillData | null {
  const parsed = skillDataSchema.safeParse(entity.metadata);
  return parsed.success ? parsed.data : null;
}

export function buildCapabilityProfilesFromEntities(params: {
  identity?: {
    brainName?: string;
    role?: string;
    purpose?: string;
    profileName?: string;
    profileDescription?: string;
    profileCategory?: ProfileCategory;
  };
  agents: BaseEntity[];
  skills: BaseEntity[];
}): { selfProfile: CapabilityProfile; networkProfiles: CapabilityProfile[] } {
  const identity = params.identity;
  const brainName = identity?.brainName ?? "This brain";
  const profileName = identity?.profileName ?? brainName;
  const purposeDescription =
    identity?.role && identity.purpose
      ? `${brainName} is ${identity.role}. Its purpose is: ${identity.purpose}.`
      : identity?.purpose;
  const descriptionParts = [
    identity?.profileDescription,
    purposeDescription,
  ].filter((value): value is string => Boolean(value?.trim()));

  const selfProfile: CapabilityProfile = {
    id: "self",
    source: "self",
    name: profileName,
    brainName,
    ...(identity?.profileCategory && { kind: identity.profileCategory }),
    ...(descriptionParts.length > 0 && {
      description: descriptionParts.join("\n\n"),
    }),
    skills: params.skills
      .map(parseSkillEntity)
      .filter((skill): skill is SkillData => skill !== null)
      .map(asProfileSkill),
  };

  const networkProfiles = params.agents
    .map((entity): CapabilityProfile | null => {
      const parsed = agentAdapter.parseAgent(entity);
      if (!parsed) return null;
      const { frontmatter, body } = parsed;
      if (frontmatter.status === "archived") return null;

      return {
        id: entity.id,
        source: "agent",
        name: frontmatter.name,
        brainName: frontmatter.brainName,
        kind: frontmatter.kind,
        status: frontmatter.status,
        ...(body.about && { description: body.about }),
        ...(body.notes && { notes: body.notes }),
        skills: body.skills.map((skill) => ({
          name: skill.name,
          description: skill.description,
          tags: normalizeTags(skill.tags),
        })),
      };
    })
    .filter((profile): profile is CapabilityProfile => profile !== null);

  return { selfProfile, networkProfiles };
}

export async function buildCapabilityProfiles(
  context: EntityPluginContext,
): Promise<{
  selfProfile: CapabilityProfile;
  networkProfiles: CapabilityProfile[];
}> {
  const [agents, skills] = await Promise.all([
    context.entityService.listEntities({
      entityType: "agent",
      options: { limit: 1000 },
    }),
    context.entityService.listEntities({
      entityType: "skill",
      options: { limit: 1000 },
    }),
  ]);

  const character = context.identity.get();
  const profile = context.identity.getProfile();
  const profileSelection = context.profileKinds.getResolved();

  return buildCapabilityProfilesFromEntities({
    identity: {
      brainName: character.name,
      role: character.role,
      purpose: character.purpose,
      profileName: profile.name,
      ...(profile.description && { profileDescription: profile.description }),
      ...(profileSelection && {
        profileCategory: profileSelection.category,
      }),
    },
    agents,
    skills,
  });
}
